import { Link } from 'react-router-dom';
import { MapPin, ChevronRight, Clock, Store } from 'lucide-react';
import { DAY_OF_WEEK_LABELS, type DayOfWeek } from '@paulownia/shared/types';

interface TodayMarketCardProps {
  marketId: string;
  marketCity: string;
  marketDayOfWeek: DayOfWeek;
  openingTime: string;
  closingTime: string;
  merchantCount: number;
  closingSoon?: boolean; // Market closes in < 30 min
}

export function TodayMarketCard({
  marketId,
  marketCity,
  marketDayOfWeek,
  openingTime,
  closingTime,
  merchantCount,
  closingSoon,
}: TodayMarketCardProps) {
  const dayLabel = DAY_OF_WEEK_LABELS[marketDayOfWeek];

  return (
    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg overflow-hidden">
      {/* Main card content */}
      <div className="p-6 text-white">
        {/* Header with market info */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{marketCity}</h2>
              <p className="text-orange-100">
                {dayLabel} • {openingTime}-{closingTime}
              </p>
            </div>
          </div>

          {/* Closing soon badge */}
          {closingSoon && (
            <div className="flex items-center gap-1 bg-white/20 text-white text-xs font-medium px-2 py-1 rounded-full">
              <Clock className="w-3 h-3" />
              <span>Ferme bientôt</span>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white/90">
            Votre marché aujourd&apos;hui
          </h3>
        </div>

        {/* Merchant count */}
        <div className="flex items-center gap-2 mb-4 bg-white/15 rounded-lg px-4 py-3">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Store className="w-4 h-4 text-white" />
          </div>
          <p className="text-sm font-medium text-white">
            {merchantCount} commerçant{merchantCount > 1 ? 's' : ''} sur ce marché
          </p>
        </div>
      </div>

      {/* CTA Button */}
      <Link
        to={`/market/${marketId}`}
        className="flex items-center justify-center gap-2 w-full bg-white/20 hover:bg-white/30 transition-colors p-4 border-t border-white/10"
      >
        <span className="font-semibold text-white text-sm">
          Voir le marché
        </span>
        <ChevronRight className="w-4 h-4 text-white" />
      </Link>
    </div>
  );
}
